import { queryOptions } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  CodePreview,
  DeskListResponse,
  ExpectedGuest,
  RegisterGuestsRequest,
  RegisterItemsRequest,
  ReleaseRequest,
  ReleaseResponse,
  Unit,
} from '@/api/pending'
import { queryKeys } from '@/api/queryKeys'

export const deskListQuery = (condoId: number, status: 'waiting' | 'done') =>
  queryOptions({
    queryKey: queryKeys.desk.list(condoId, status),
    // 204 (no waiting/done entries) comes back as `undefined`.
    queryFn: ({ signal }) =>
      api
        .get<DeskListResponse>('/condominiums/:id/receivings/desk', {
          params: { id: condoId },
          query: { status },
          signal,
        })
        .then((d) => d ?? { entries: [] }),
  })

export const expectedGuestsQuery = (condoId: number) =>
  queryOptions({
    // No `date`: every upcoming expected guest. The Esperados tab lists them all, and the stat
    // tile counts the ones for today.
    queryKey: queryKeys.desk.expected(condoId, 'all'),
    queryFn: ({ signal }) =>
      api
        .get<ExpectedGuest[]>('/condominiums/:id/receivings/expected-guests', {
          params: { id: condoId },
          signal,
        })
        .then((d) => d ?? []),
  })

/** Units with their residents: the UnitPad and the "no resident linked" warning. */
export const unitsWithResidentsQuery = (condoId: number) =>
  queryOptions({
    queryKey: [...queryKeys.units(condoId), 'residents'] as const,
    queryFn: ({ signal }) =>
      api
        .get<Unit[]>('/condominiums/:id/units', {
          params: { id: condoId },
          query: { include: 'residents' },
          signal,
        })
        .then((d) => d ?? []),
  })

/** Resolves a resident's code to a unit's waiting batch or a guest. 400 invalid, 409 already used. */
export const previewCode = (condoId: number, code: string) =>
  api.get<CodePreview>('/condominiums/:id/receivings/codes/:code', {
    params: { id: condoId, code },
  })

export const releaseCode = (condoId: number, code: string) =>
  api.post<ReleaseResponse>('/condominiums/:id/receivings/codes/:code/release', undefined, {
    params: { id: condoId, code },
  })

export const registerItems = (condoId: number, req: RegisterItemsRequest) =>
  api.post<{ registered: number }>('/condominiums/:id/receivings/items', req, {
    params: { id: condoId },
  })

export const registerGuests = (condoId: number, req: RegisterGuestsRequest) =>
  api.post<{ registered: number }>('/condominiums/:id/receivings/guests', req, {
    params: { id: condoId },
  })

/** Fallback to the code release: confirm one item (call once per item of the batch). 400 wrong code, 409 already confirmed. */
export const confirmItem = (condoId: number, unitId: number, itemId: string, code: string) =>
  api.post<void>(
    '/condominiums/:id/units/:unitId/receivings/items/:itemId/confirm',
    { code } satisfies ReleaseRequest,
    { params: { id: condoId, unitId, itemId } },
  )

export const confirmGuest = (condoId: number, unitId: number, arrivalId: string, code: string) =>
  api.post<void>(
    '/condominiums/:id/units/:unitId/receivings/guests/:arrivalId/confirm',
    { code } satisfies ReleaseRequest,
    { params: { id: condoId, unitId, arrivalId } },
  )
